'use client';

import { c as _c } from "react/compiler-runtime";
import { type Post } from '@shared/all/domains/posts';
import usePostsShared from '@shared/utils/hooks/usePosts';
import { useInfiniteQuery } from '@tanstack/react-query';
import { ChevronRight, History } from 'lucide-react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import Button from '../../components/common/Button';
import GoBackButton from '../../components/factiiis/general/GoBackButton';
import CardActivity from '../../components/pages/space/CardActivity';
import PostItem from '../../components/posts/PostItem';
import RepliesContainer from '../../components/posts/RepliesContainer';
import AppLayout from '../../layouts/AppLayout/AppLayout';
import { contentToPlainText } from '../../utils/contentText';
import { client, type RouterOutputs } from '../../utils/trpc';
type PostPageProps = {
  initialData?: RouterOutputs['posts']['singlePost'];
  postId: string;
  initialError?: boolean;
  isEmbed?: boolean;
};
const PostPage = t0 => {
  const $ = _c(49);
  const {
    initialData,
    postId,
    initialError,
    isEmbed: t1
  } = t0;
  const isEmbed = t1 === undefined ? false : t1;
  const router = useRouter();
  const {
    queryKey
  } = usePostsShared(client);
  const {
    isPolitics,
    isNsfw
  } = usePostsShared(client);
  let t2;
  if ($[0] !== postId) {
    t2 = async () => {
      const [error, result] = await client.posts.singlePost.query({
        postId
      });
      if (error) {
        throw error;
      }
      return result;
    };
    $[0] = postId;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = Boolean(postId) && !initialError;
  const t4 = !initialData;
  let t5;
  if ($[2] !== initialData) {
    t5 = initialData ? {
      pages: [initialData],
      pageParams: [0]
    } : undefined;
    $[2] = initialData;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  let t6;
  if ($[4] !== queryKey || $[5] !== t2 || $[6] !== t3 || $[7] !== t4 || $[8] !== t5) {
    t6 = {
      queryKey,
      queryFn: t2,
      initialPageParam: 0,
      getNextPageParam: _temp,
      enabled: t3,
      refetchOnMount: t4,
      initialData: t5,
      refetchOnWindowFocus: false,
      staleTime: 300000
    };
    $[4] = queryKey;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const {
    data
  } = useInfiniteQuery(t6);
  const renderData = data;
  const renderPostDetails = renderData?.pages[0]?.posts[0];
  const renderParentPost = renderPostDetails?.parentPost || renderPostDetails;
  let t7;
  if ($[10] !== renderPostDetails) {
    t7 = renderPostDetails?.parentPost ? {
      ...renderPostDetails.parentPost,
      replies: [renderPostDetails]
    } : renderPostDetails;
    $[10] = renderPostDetails;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const renderThread = t7;
  const postSpaceSlug = renderParentPost?.space ?? renderPostDetails?.space;
  const isBoardPost = !!renderPostDetails?.pos;
  const postAuthor = renderPostDetails?.user?.username;
  let t8;
  if ($[12] !== renderPostDetails?.content) {
    t8 = contentToPlainText(renderPostDetails?.content).slice(0, 120);
    $[12] = renderPostDetails?.content;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const postSnippet = t8;
  const postHeading = postAuthor ? `Post by @${postAuthor}${postSnippet ? `: ${postSnippet}` : ""}` : "Post on Factiii";
  let t9;
  if ($[14] !== isBoardPost || $[15] !== isEmbed) {
    t9 = isEmbed || !isBoardPost ? undefined : {
      items: []
    };
    $[14] = isBoardPost;
    $[15] = isEmbed;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== isBoardPost || $[18] !== isEmbed || $[19] !== postId) {
    t10 = isEmbed || !isBoardPost ? undefined : {
      items: [{
        icon: <History />,
        text: "Activity",
        component: <CardActivity postId={postId} />
      }]
    };
    $[17] = isBoardPost;
    $[18] = isEmbed;
    $[19] = postId;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== postHeading || $[22] !== renderPostDetails) {
    t11 = renderPostDetails && <h1 className="sr-only">{postHeading}</h1>;
    $[21] = postHeading;
    $[22] = renderPostDetails;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <GoBackButton text="Home" />;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== postSpaceSlug) {
    t13 = postSpaceSlug && <Link href={`/s/${postSpaceSlug}`} className="flex items-center gap-1 text-sm font-emphasis text-textPrimary/60 hover:text-textSecondary/70"><span>Go to s/{postSpaceSlug}</span><ChevronRight size={16} className="text-current" /></Link>;
    $[25] = postSpaceSlug;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== t11 || $[28] !== t13) {
    t14 = <div className="flex items-center justify-between">{t11}{t12}{t13}</div>;
    $[27] = t11;
    $[28] = t13;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== isBoardPost || $[31] !== isNsfw || $[32] !== isPolitics || $[33] !== renderParentPost || $[34] !== renderPostDetails || $[35] !== renderThread) {
    t15 = renderParentPost && renderPostDetails && <div><PostItem disableLink={true} isFullLength={true} isInView={true} post={renderParentPost} isThread={Boolean(renderPostDetails.parentPostId)} />{isBoardPost && <div className="mt-4 rounded-lg bg-background-secondary p-4 shadow-sm lg:hidden"><h3 className="mb-3 text-sm font-emphasis text-textPrimary">Activity</h3><CardActivity postId={renderPostDetails.id} /></div>}{renderThread && <RepliesContainer disableLink={false} isFullLength={true} isPoliticsParent={isPolitics(renderThread.postFactiiisCount, renderThread.space)} isNsfwParent={isNsfw(renderThread.postFactiiisCount)} post={renderThread as Post} threadId={renderPostDetails.parentThreadId} />}</div>;
    $[30] = isBoardPost;
    $[31] = isNsfw;
    $[32] = isPolitics;
    $[33] = renderParentPost;
    $[34] = renderPostDetails;
    $[35] = renderThread;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  let t16;
  if ($[37] !== initialError || $[38] !== router) {
    t16 = initialError && <div className="mt-4 flex size-full flex-col items-center justify-center rounded-md bg-background-secondary py-10 shadow-md"><p className="mb-4 text-lg font-semibold">Post Not Found</p><Button text="Go Home" onClick={() => router.push("/")} classNames="hover:border-2 hover:border-ds-border-standard" /></div>;
    $[37] = initialError;
    $[38] = router;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  let t17;
  if ($[40] !== t14 || $[41] !== t15 || $[42] !== t16) {
    t17 = <section className="space-y-4">{t14}{t15}{t16}</section>;
    $[40] = t14;
    $[41] = t15;
    $[42] = t16;
    $[43] = t17;
  } else {
    t17 = $[43];
  }
  let t18;
  if ($[44] !== isEmbed || $[45] !== t10 || $[46] !== t17 || $[47] !== t9) {
    t18 = <AppLayout isEmbed={isEmbed} leftSidebar={t9} rightSidebar={t10}>{t17}</AppLayout>;
    $[44] = isEmbed;
    $[45] = t10;
    $[46] = t17;
    $[47] = t9;
    $[48] = t18;
  } else {
    t18 = $[48];
  }
  return t18;
};
export default PostPage;
function _temp() {}